import {
  chatEventEmitter,
  InitChatSdkParams,
  chatSdk,
  SubscribeToChatroomsListParams,
  ReconnectSdkParams,
  SubscribeToChatroomParams,
  IChatroom,
  UnreadChatroomCountFilter,
  GlobalUnreadsCountCallbackArgs,
  UnseenChatroomCountFilter,
  UnseenChatroomCount,
} from '@wix/chat-sdk';
import { WorkerResultCommand } from '@wix/da-shared-react/pkg/ChatV2/worker/commands';
import PostMessageable from '@wix/da-shared-react/pkg/ChatV2/worker/postMessageable';
import {
  ChatWorkerEgressCommand,
  ChatWorkerIngressCommand,
} from '@wix/da-shared-react/pkg/ChatV2/worker/types';
import throttle from 'lodash/throttle';
import {
  deserializeMessage,
  MessageTransferDto,
  serializeMessage,
  serializeRoom,
} from '@wix/da-shared-react/pkg/ChatV2/worker/serializer';
import { CHAT_EVENTS } from '@wix/chat-sdk/lib/src/services/chat-event-emitter.service';

export class ChatWorker extends PostMessageable {
  private throttle: number = 500;
  private subscriptions: Map<string, any> = new Map();

  /**
   * Bind all ingress listeners to their handlers
   */
  startup() {
    this.listenFor(
      ChatWorkerIngressCommand.WORKER_SET_THROTTLE,
      (id, data: any) => this.setThrottle(data.throttle)
    );
    this.listenFor<InitChatSdkParams>(
      ChatWorkerIngressCommand.INIT,
      this.initSdk.bind(this)
    );
    this.listenFor(ChatWorkerIngressCommand.CLEAR, this.clearSdk.bind(this));
    this.listenFor(
      ChatWorkerIngressCommand.DISCONNECT,
      this.disconnectSdk.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.RECONNECT,
      this.reconnectSdk.bind(this)
    );
    this.listenFor<SubscribeToChatroomsListParams>(
      ChatWorkerIngressCommand.CHATLIST_SUB,
      this.subscribeToChatRoomsList.bind(this)
    );
    this.listenFor<SubscribeToChatroomParams>(
      ChatWorkerIngressCommand.CHATROOM_SUB,
      this.subscribeToChatroom.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_SUB_GLOBAL_UNREAD,
      this.subscribeToGlobalUnreadStatus.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_UNSEEN_SUB,
      this.subscribeToUnseenCount.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_UNSEEN_MARK_SEEN,
      this.unseenMarkSeen.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_SEND_MSG,
      this.sendMessage.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_ADD_MSG,
      this.addMessage.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_DELETE_MSG,
      this.deleteMessage.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_NOTIFY_TYPING,
      this.notifyTyping.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_NOTIFY_TYPING_TEXT_CHANGED,
      this.notifyTypingTextChanged.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_DELETE_CHATROOM,
      this.deleteChatroom.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_DELETE_CHATROOMS,
      this.deleteChatrooms.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_SET_LAST_MESSAGE_UNREAD,
      this.setLastMessageUnread.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_SET_MESSAGES_READ,
      this.setMessagesRead.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_SET_ALL_ROOMS_READ,
      this.setAllRoomsRead.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_SET_ROOMS_MESSAGES_READ,
      this.setRoomsMessagesRead.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_GET_MESSAGES_PAGE,
      this.getMessagesPage.bind(this)
    );
    this.listenFor(
      ChatWorkerIngressCommand.CHAT_LISTEN_REALTIME_MESSAGES,
      this.listenToRealtimeMessages.bind(this)
    );

    // Chat sdk events
    chatSdk.onConnecting(() => {
      this.sendPostMsg(
        '',
        this._factorySuccessResult(ChatWorkerEgressCommand.EVENT_CONNECTING)
      );
    });
    chatSdk.onConnectionEstablished(() => {
      this.sendPostMsg(
        '',
        this._factorySuccessResult(ChatWorkerEgressCommand.EVENT_ESTABLISHED)
      );
    });
    chatSdk.onDisconnect(() => {
      this.sendPostMsg(
        '',
        this._factorySuccessResult(ChatWorkerEgressCommand.EVENT_DISCONNECT)
      );
    });

    this.sendPostMsg(
      '',
      this._factorySuccessResult(ChatWorkerEgressCommand.WORKER_READY)
    );
  }

  setThrottle(throttleTime) {
    this.throttle = throttleTime;
  }

  initSdk(cmdId: string, params: InitChatSdkParams) {
    try {
      chatSdk.init(params);
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(ChatWorkerEgressCommand.INIT_RESULT)
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(ChatWorkerEgressCommand.INIT_RESULT, {
          error: err.message,
        })
      );
    }
  }

  clearSdk() {
    chatSdk.clear();
  }

  disconnectSdk() {
    chatSdk.disconnect();
  }

  reconnectSdk(cmdId: string, params: ReconnectSdkParams) {
    try {
      chatSdk.reconnect(params);
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(ChatWorkerEgressCommand.RECONNECT_RESULT)
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(ChatWorkerEgressCommand.RECONNECT_RESULT, {
          error: err.message,
        })
      );
    }
  }

  subscribeToChatRoomsList(
    cmdId: string,
    params: SubscribeToChatroomsListParams
  ) {
    const throttledCallback = throttle(data => {
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHATLIST_SUB_CB_RESULT,
          {
            cursor: data.cursor,
            rooms: data.rooms.map(serializeRoom),
            status: data.status,
          }
        )
      );
    }, this.throttle);

    const fetchMore = (id, { cursor }) => {
      const subby = this.subscriptions.get(id);
      if (subby && subby.fetchMore) {
        subby.fetchMore(cursor);
      }
    };

    const unsubscribe = id => {
      const subby = this.subscriptions.get(id);
      if (subby && subby.unsubscribe) {
        subby.unsubscribe();
      }
      this.unlistenFor(ChatWorkerIngressCommand.CHATLIST_FETCHMORE, fetchMore);
      this.unlistenFor(ChatWorkerIngressCommand.CHATLIST_UNSUB, unsubscribe);
    };

    try {
      const sub = chatSdk.subscribeToChatRoomsList({
        ...params,
        callback: throttledCallback,
      });
      this.registerSubscription(cmdId, sub);
      this.listenFor(ChatWorkerIngressCommand.CHATLIST_FETCHMORE, fetchMore);
      this.listenFor(ChatWorkerIngressCommand.CHATLIST_UNSUB, unsubscribe);

      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHATLIST_SUB_RESULT,
          { subscriberId: sub.subscriberId }
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(ChatWorkerEgressCommand.CHATROOM_SUB_RESULT, {
          error: err.message,
        })
      );
    }
  }

  subscribeToChatroom(cmdId: string, params: SubscribeToChatroomParams) {
    const onChange = throttle((room: IChatroom) => {
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(ChatWorkerEgressCommand.CHATROOM_ON_CHANGE, {
          ...room,
          messages: room.messages.map(serializeMessage),
        })
      );
    }, this.throttle);

    const onInitialFetchDone = throttle((room: IChatroom) => {
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHATROOM_ON_INITIAL_FETCH,
          {
            ...room,
            messages: room.messages.map(serializeMessage),
          }
        )
      );
    }, this.throttle);

    const onNewMessage = throttle((room: IChatroom) => {
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHATROOM_ON_NEW_MESSAGE,
          {
            ...room,
            messages: room.messages.map(serializeMessage),
          }
        )
      );
    }, this.throttle);

    const onError = throttle(data => {
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHATROOM_ON_ERROR,
          data
        )
      );
    }, this.throttle);

    const fetchMore = (id, { cursor }) => {
      const subby = this.subscriptions.get(id);
      if (subby && subby.fetchMore) {
        subby.fetchMore(cursor);
      }
    };

    const unsubscribe = id => {
      const subby = this.subscriptions.get(id);
      if (subby && subby.unsubscribe) {
        subby.unsubscribe();
      }
      this.unlistenFor(ChatWorkerIngressCommand.CHATROOM_FETCHMORE, fetchMore);
      this.unlistenFor(ChatWorkerIngressCommand.CHATROOM_UNSUB, unsubscribe);
    };

    try {
      const sub = chatSdk.subscribeToChatRoom({
        chatroomId: params.chatroomId,
        onChange,
        onInitialFetchDone,
        onNewMessage,
        onError,
      });
      this.registerSubscription(cmdId, sub);
      this.listenFor(ChatWorkerIngressCommand.CHATROOM_FETCHMORE, fetchMore);
      this.listenFor(ChatWorkerIngressCommand.CHATROOM_UNSUB, unsubscribe);

      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(ChatWorkerEgressCommand.CHATROOM_SUB_RESULT)
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(ChatWorkerEgressCommand.CHATROOM_SUB_RESULT, {
          error: err.message,
        })
      );
    }
  }

  subscribeToGlobalUnreadStatus(
    cmdId: string,
    {
      filter,
    }: {
      filter?: UnreadChatroomCountFilter;
    }
  ) {
    const throttledCallback = throttle(
      (data: GlobalUnreadsCountCallbackArgs) => {
        this.sendPostMsg(
          cmdId,
          this._factorySuccessResult(
            ChatWorkerEgressCommand.CHAT_GLOBAL_UNREAD_CHANGE,
            data
          )
        );
      },
      this.throttle
    );

    const unsubscribe = id => {
      const subby = this.subscriptions.get(id);
      if (subby && subby.unsubscribe) {
        subby.unsubscribe();
      }
      this.unlistenFor(
        ChatWorkerIngressCommand.CHAT_UNSUB_GLOBAL_UNREAD,
        unsubscribe
      );
    };

    try {
      const sub = chatSdk.subscribeToGlobalUnreadStatus({
        callback: throttledCallback,
        filter,
      });
      this.registerSubscription(cmdId, sub);
      this.listenFor(
        ChatWorkerIngressCommand.CHAT_UNSUB_GLOBAL_UNREAD,
        unsubscribe
      );

      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_GLOBAL_UNREAD_SUB_RESULT
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_GLOBAL_UNREAD_SUB_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  subscribeToUnseenCount(cmdId: string, filter: UnseenChatroomCountFilter) {
    const throttledCallback = throttle((data: UnseenChatroomCount) => {
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_UNSEEN_CHANGE,
          data
        )
      );
    }, this.throttle);

    const unsubscribe = id => {
      const subby = this.subscriptions.get(id);
      if (subby && subby.unsubscribe) {
        subby.unsubscribe();
      }
      this.unlistenFor(ChatWorkerIngressCommand.CHAT_UNSEEN_UNSUB, unsubscribe);
    };

    try {
      const sub = chatSdk.unseenChatroom.subscribeToCount(
        throttledCallback,
        filter
      );
      this.registerSubscription(cmdId, sub);
      this.listenFor(ChatWorkerIngressCommand.CHAT_UNSEEN_UNSUB, unsubscribe);

      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_UNSEEN_SUB_RESULT
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_UNSEEN_SUB_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  unseenMarkSeen(cmdId: string, filter: UnseenChatroomCountFilter) {
    try {
      chatSdk.unseenChatroom.markChatroomsAsSeen(filter);
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_UNSEEN_MARKED_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_UNSEEN_MARKED_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  sendMessage(cmdId: string, message: MessageTransferDto) {
    try {
      chatSdk.sendMessage(deserializeMessage(message));
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_SEND_MSG_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(ChatWorkerEgressCommand.CHAT_SEND_MSG_RESULT, {
          error: err.message,
        })
      );
    }
  }

  addMessage(cmdId: string, message: MessageTransferDto) {
    try {
      chatSdk.addMessage(deserializeMessage(message));
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_ADD_MSG_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(ChatWorkerEgressCommand.CHAT_ADD_MSG_RESULT, {
          error: err.message,
        })
      );
    }
  }

  deleteMessage(cmdId: string, messageId: string) {
    try {
      chatSdk.deleteMessage(messageId);
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_DELETE_MSG_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_DELETE_MSG_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  notifyTyping(cmdId: string, { chatroomId, text }) {
    try {
      chatSdk.notifyTyping(chatroomId, text);
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_NOTIFY_TYPING_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_NOTIFY_TYPING_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  notifyTypingTextChanged(cmdId: string, { chatroomId, text, isActive }) {
    try {
      chatSdk.notifyTypingTextChange(chatroomId, text, isActive);
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_NOTIFY_TYPING_TEXT_CHANGE_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_NOTIFY_TYPING_TEXT_CHANGE_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  deleteChatroom(cmdId: string, { chatroomId }) {
    try {
      chatSdk.deleteChatroom(chatroomId);
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_DELETE_CHATROOM_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_DELETE_CHATROOM_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  deleteChatrooms(cmdId: string, { chatroomIds }) {
    try {
      chatSdk.deleteChatrooms(chatroomIds);
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_DELETE_CHATROOMS_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_DELETE_CHATROOMS_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  setLastMessageUnread(cmdId: string, { chatroomId }) {
    try {
      chatSdk.setLastMessageUnread({ chatroomId });
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_SET_LAST_MESSAGE_UNREAD_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_SET_LAST_MESSAGE_UNREAD_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  setMessagesRead(cmdId: string, { chatroomId }) {
    try {
      chatSdk.setMessagesRead({ chatroomId });
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_SET_MESSAGES_READ_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_SET_MESSAGES_READ_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  setAllRoomsRead(cmdId: string, filter) {
    try {
      chatSdk.setAllRoomsAsRead(filter);
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_SET_ALL_ROOMS_AS_READ_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_SET_ALL_ROOMS_AS_READ_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  setRoomsMessagesRead(cmdId: string, { chatroomIds }) {
    try {
      chatSdk.setRoomsMessagesRead(chatroomIds);
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_SET_ROOMS_MESSAGES_READ_RESULT,
          undefined
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_SET_ROOMS_MESSAGES_READ_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  async getMessagesPage(
    cmdId: string,
    { chatroomId, messageSequence, pageSize }
  ) {
    try {
      const messages = await chatSdk.getMessagesPage({
        chatroomId,
        messageSequence,
        pageSize,
      });
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_GET_MESSAGES_PAGE_RESULT,
          { messages: messages.map(serializeMessage) }
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_GET_MESSAGES_PAGE_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  listenToRealtimeMessages(cmdId: string) {
    const throttledCallback = throttle((data: UnseenChatroomCount) => {
      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_REALTIME_MESSAGE_RESULT,
          data
        )
      );
    }, this.throttle);

    const unsubscribe = id => {
      const subby = this.subscriptions.get(id);
      if (subby && subby.unsubscribe) {
        subby.unsubscribe();
      }
      this.unlistenFor(
        ChatWorkerIngressCommand.CHAT_REALTIME_MESSAGES_UNSUB,
        unsubscribe
      );
    };

    try {
      chatEventEmitter.addListener(
        CHAT_EVENTS.NEW_REALTIME_MESSAGE,
        throttledCallback
      );
      this.registerSubscription(cmdId, {
        unsubscribe: () => {
          chatEventEmitter.removeListener(
            CHAT_EVENTS.NEW_REALTIME_MESSAGE,
            throttledCallback
          );
        },
      });
      this.listenFor(
        ChatWorkerIngressCommand.CHAT_REALTIME_MESSAGES_UNSUB,
        unsubscribe
      );

      this.sendPostMsg(
        cmdId,
        this._factorySuccessResult(
          ChatWorkerEgressCommand.CHAT_REALTIME_MESSAGES_SUB_RESULT
        )
      );
    } catch (err) {
      this.sendPostMsg(
        cmdId,
        this._factoryFailResult(
          ChatWorkerEgressCommand.CHAT_REALTIME_MESSAGES_SUB_RESULT,
          {
            error: err.message,
          }
        )
      );
    }
  }

  private _factorySuccessResult(type, payload?) {
    return new WorkerResultCommand(type, true, payload);
  }

  private _factoryFailResult(type, payload?) {
    return new WorkerResultCommand(type, false, payload);
  }

  private registerSubscription(subId, sub) {
    this.subscriptions.set(subId, sub);
    return subId;
  }
}

export const chatWorkerInstance = new ChatWorker();
